import {
  Control,
  Controller,
  FieldValues,
  Path,
  RegisterOptions,
} from "react-hook-form";

import { cn } from "../../utls/utils";
import Input, { InputProps } from "../input";
import Typography from "../typography";

export interface ThemedTextFieldProps<TFieldValues extends FieldValues>
  extends Omit<InputProps, "name" | "value" | "onChange"> {
  name: Path<TFieldValues>;
  control: Control<TFieldValues>;
  rules?: RegisterOptions<TFieldValues, Path<TFieldValues>>;
  wrapperClassName?: string;
  onValueChange?: (value: string) => void;
  valueTransformer?: (value: string) => string;
  showCharacterCount?: boolean;
}

export function ThemedTextField<TFieldValues extends FieldValues>({
  name,
  control,
  rules,
  wrapperClassName,
  onValueChange,
  valueTransformer,
  showCharacterCount = false,
  helperText,
  error,
  onBlur: customOnBlur,
  ...inputProps
}: ThemedTextFieldProps<TFieldValues>) {
  const maxLength = inputProps.maxLength;

  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field, fieldState }) => {
        const rawValue = field.value;
        const value =
          typeof rawValue === "string"
            ? rawValue
            : rawValue === undefined || rawValue === null
            ? ""
            : String(rawValue);

        const resolvedError = fieldState.error?.message ?? error;
        const resolvedHelperText = !resolvedError ? helperText : undefined;

        return (
          <div className={cn("flex flex-col", wrapperClassName)}>
            <Input
              {...inputProps}
              ref={field.ref}
              name={field.name}
              value={value}
              onChange={(event) => {
                const nextValue = event.target.value;
                const transformedValue = valueTransformer
                  ? valueTransformer(nextValue)
                  : nextValue;

                field.onChange(transformedValue);
                onValueChange?.(transformedValue);
              }}
              onBlur={(event) => {
                field.onBlur();
                customOnBlur?.(event);
              }}
              helperText={resolvedHelperText}
              error={resolvedError}
            />

            {showCharacterCount && (
              <Typography
                variant="caption"
                className={cn(
                  "mt-1 text-right",
                  resolvedError ? "text-red-500" : "text-terracotta-600"
                )}
              >
                {value.length}
                {typeof maxLength === "number" ? `/${maxLength}` : null}
              </Typography>
            )}
          </div>
        );
      }}
    />
  );
}

export default ThemedTextField;
